"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  scanRenderedComponents,
  type RenderedComponent,
} from "../utils/scan-rendered-components";

export type { RenderedComponent };

export function useRenderedComponents(pathname: string) {
  const [components, setComponents] = useState<RenderedComponent[]>([]);
  const [scanning, setScanning] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const scan = useCallback(() => {
    setScanning(true);
    setTimeout(() => {
      const results = scanRenderedComponents(pathname);
      if (mountedRef.current) {
        setComponents(results);
        setScanning(false);
      }
    }, 0);
  }, [pathname]);

  // Auto-scan on pathname change
  useEffect(() => {
    const frame = requestAnimationFrame(scan);
    return () => cancelAnimationFrame(frame);
  }, [scan]);

  // Auto-scan on DOM mutations (portals, dropdown menus, modals, sheets)
  useEffect(() => {
    if (typeof window === "undefined") return;

    let timeoutId: NodeJS.Timeout;
    const observer = new MutationObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        scan();
      }, 150);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [scan]);

  return { components, scanning, scan };
}
